import { Component, HostBinding, OnDestroy, ViewEncapsulation } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatDialog } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { HttpErrorResponse } from '@angular/common/http';
import { NgFor, NgIf, UpperCasePipe } from '@angular/common';
import { firstValueFrom, Subscription } from 'rxjs';
import { TranslateModule } from '@ngx-translate/core';
import { CdkScrollable } from '@angular/cdk/scrolling';
import { MatIconModule } from '@angular/material/icon';

import { Guarantee } from '../../models/guarantee';
import { pageTransitions } from '../../../shared/animations/page-transitions';
import { UNDERSCORE } from '../../../shared/utils/constant';
import { GuaranteeStatus } from '../../models/guarantee-constants';
import { getGuaranteeRequestStatusTr } from '../../utils/guarantee.utils';
import { TranslationService } from '../../../shared/services/translation.service';
import { DatePipe } from '../../../shared/pipes/date.pipe';
import { ErrorService } from '../../../shared/services/error.service';
import {
	FeedbackErrorComponent
} from '../../../shared/components/feedback-error/feedback-error.component';
import { GuaranteeRequestSummaryComponent } from '../guarantee-request-summary/guarantee-request-summary.component';
import { BackButtonComponent } from '../../../shared/components/back-button/back-button.component';
import { safe } from '../../../shared/utils/safe';
import { GuaranteeRequestAttachment } from '../../models/guarantee-request-attachment';
import { GuaranteeRequestService } from '../../services/guarantee-request.service';
import { SafeUrlPipe } from '../../../shared/pipes/safeurl.pipe';
import { DialogDeleteDraftComponent, RESPONSE_CONFIRM } from
	'../utils/dialog-delete-draft/dialog-delete-draft.component';
import { SnackbarDeleteDraftComponent } from
	'../../../claim/components/utils/snackbar-delete-draft/snackbar-delete-draft.component';
import { LoadingDialogComponent } from '../entry/step-4/loading-dialog.component';
import {
	DialogRequestCertificateComponent,
	DialogRequestCertificateData
} from '../utils/dialog-request-certificate/dialog-request-certificate.component';

@Component({
	selector: 'copo-guarantee-request-card',
	templateUrl: './guarantee-request-card.component.html',
	styleUrl: './guarantee-request-card.component.css',
	animations: pageTransitions,
	encapsulation: ViewEncapsulation.Emulated,
	imports: [
		CdkScrollable,
		BackButtonComponent,
		TranslateModule,
		GuaranteeRequestSummaryComponent,
		UpperCasePipe,
		DatePipe,
		SafeUrlPipe,
		MatButtonModule,
		MatIconModule,
		NgFor,
		NgIf,
		FeedbackErrorComponent
	]
})

export class GuaranteeRequestCardComponent implements OnDestroy {
	@HostBinding('class.passthrough') readonly passthrough = true;
	guarantee: Guarantee;
	requestingCertificate = false;
	readonly UNDERSCORE = UNDERSCORE;
	deleteDraftSubscription?: Subscription;

	constructor(
		private readonly route: ActivatedRoute,
		private readonly router: Router,
		private readonly translationService: TranslationService,
		private readonly guaranteeService: GuaranteeRequestService,
		private readonly errorService: ErrorService,
		private readonly dialog: MatDialog,
		private readonly snackBar: MatSnackBar,
	) {
		const res = <{ guarantee: Guarantee }> this.route.snapshot.data;
		this.errorService.resetErrorMsg();
		this.guarantee = res['guarantee'];
	}

	ngOnDestroy() {
		this.deleteDraftSubscription?.unsubscribe();
		this.errorService.resetErrorMsg();
	}

	getGuaranteeRequestStatus(status: GuaranteeStatus) {
		return getGuaranteeRequestStatusTr(this.translationService, status);
	}

	async requestCertificate(): Promise<void> {
		if (this.requestingCertificate || !this.guarantee.id || !this.isSatisfied(this.guarantee)) {
			return;
		}
		this.errorService.resetErrorMsg();

		const confirmation = this.dialog.open<DialogRequestCertificateComponent, DialogRequestCertificateData, boolean>(
			DialogRequestCertificateComponent,
			{
				data: { guaranteeName: this.guarantee.name },
				panelClass: 'copo-claim-dialog',
				autoFocus: false
			}
		);
		const confirmed = await firstValueFrom(confirmation.afterClosed());
		if (confirmed !== true) {
			return;
		}

		this.requestingCertificate = true;
		const loadingDialog = this.dialog.open<LoadingDialogComponent>(
			LoadingDialogComponent,
			{ disableClose: true, minWidth: '256px', autoFocus: false }
		);
		let requestSucceeded = false;
		try {
			await this.guaranteeService.requestCertificate(this.guarantee.id);
			const refreshedGuarantee = await firstValueFrom(
				this.guaranteeService.getGuaranteeById(this.guarantee.id)
			);
			const { certificates, crmAttachments } = this.guarantee;
			Object.assign(this.guarantee, refreshedGuarantee);
			this.guarantee.certificates = certificates;
			this.guarantee.crmAttachments = crmAttachments;
			requestSucceeded = true;
		} catch (err: unknown) {
			console.error('unable to request guarantee certificate', err);
			this.errorService.setErrorMsg(
				this.translationService.getTR(
					err instanceof HttpErrorResponse && err.status === 409
						? 'guarantee.certificate.request.unavailable'
						: 'guarantee.certificate.request.failed'
				),
				'quapp.error'
			);
		} finally {
			loadingDialog.close();
			this.requestingCertificate = false;
		}
		if (requestSucceeded) {
			this.snackBar.open(
				this.translationService.getTR('guarantee.certificate.request.success'),
				undefined,
				{ duration: 5000 }
			);
		}
	}

	isDraft(guarantee: Guarantee) {
		return guarantee.status === GuaranteeStatus.DRAFT;
	}

	async editDraft(draftId: string) {
		await this.router.navigate(
			['/guarantee-requests/edit', draftId, 'project-characteristics'],
			{ replaceUrl: true }
		);
	}

	evtDeleteDraft(draft: Guarantee) {
		const path = '/guarantee-requests/drafts';
		const sub = this.dialog.open<DialogDeleteDraftComponent, number>(
			DialogDeleteDraftComponent,
			{ panelClass: 'copo-claim-dialog' }
		);
		sub.componentInstance.draftName = draft.name;
		this.deleteDraftSubscription = sub.afterClosed().subscribe(async response => {
			if (response === RESPONSE_CONFIRM && draft.id !== undefined) {
				try {
					await this.guaranteeService.deleteGuaranteeRequest(draft.id);
					this.snackBar.openFromComponent(SnackbarDeleteDraftComponent, {
						duration: 5000,
						panelClass: ['copo-draft-snackbar']
					});
					await this.router.navigate(['redirect'], { queryParams: { path }, replaceUrl: true });
				} catch (err) {
					console.error('unable to delete draft', err);
					this.snackBar.open(
						this.translationService.getTR('error.deleting.draft'),
						undefined,
						{ duration: 5000 }
					);
					await this.router.navigate(['redirect'], { queryParams: { path }, replaceUrl: true });
				}
			}
		});
	}

	isOpen(guarantee: Guarantee) {
		return guarantee.status === GuaranteeStatus.OPEN;
	}

	isOther(guarantee: Guarantee) {
		return guarantee.status === GuaranteeStatus.LAUNCH_ANALYSIS;
	}

	isSatisfied(guarantee: Guarantee) {
		return guarantee.status === GuaranteeStatus.SATISFIED;
	}

	isCertificateRequested(guarantee: Guarantee) {
		return guarantee.status === GuaranteeStatus.CERTIFICATE_REQUESTED;
	}

	isCertificateIssued(guarantee: Guarantee) {
		return guarantee.status === GuaranteeStatus.CERTIFICATE_ISSUED;
	}

	isRejected(guarantee: Guarantee) {
		return guarantee.status === GuaranteeStatus.REJECTED_BY_SUPLIER;
	}

	isCancelled(guarantee: Guarantee) {
		return guarantee.status === GuaranteeStatus.CANCELLED;
	}

	isUnknown(guarantee: Guarantee) {
		return !this.isDraft(guarantee)
			&& !this.isOpen(guarantee)
			&& !this.isOther(guarantee)
			&& !this.isSatisfied(guarantee)
			&& !this.isCertificateRequested(guarantee)
			&& !this.isCertificateIssued(guarantee)
			&& !this.isRejected(guarantee)
			&& !this.isCancelled(guarantee);
	}

	getCertificates(guarantee: Guarantee) {
		return Object.values(guarantee.certificates).sort((left, right) => {
			const leftTime = Date.parse(left.creationDateTime ?? '');
			const rightTime = Date.parse(right.creationDateTime ?? '');
			return (Number.isNaN(rightTime) ? 0 : rightTime) - (Number.isNaN(leftTime) ? 0 : leftTime);
		});
	}

	getProposalsAndOtherDocuments(guarantee: Guarantee) {
		return Object.values(guarantee.crmAttachments);
	}

	getAttachmentUrl(id: string, attachment: GuaranteeRequestAttachment) {
		return attachment.localUrl
			|| this.guaranteeService.getDownloadFile(id, safe(attachment.chronicleId));
	}

	async showAttachment(event: MouseEvent, attachment: GuaranteeRequestAttachment) {
		await this.guaranteeService.showAttachment(event, attachment);
	}
}
